import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { ShieldCheck, Lock, Accessibility, FileText, Mail, Scale, Users } from "lucide-react";

const SECTIONS = [
  { id: "ada", label: "ADA / Accessibility", icon: Accessibility },
  { id: "privacy", label: "Privacy", icon: Lock },
  { id: "hipaa", label: "HIPAA", icon: ShieldCheck },
  { id: "security", label: "Security Controls", icon: ShieldCheck },
  { id: "equity", label: "Equity Commitment", icon: Scale },
  { id: "deia", label: "DEIA Policy", icon: Users },
  { id: "terms", label: "Terms of Use", icon: FileText },
];

const DEIA_COMMITMENTS = [
  ["Equitable Evaluation Practices", "PHMEG integrates DEIA principles into all stages of evaluation — design, data collection, analysis, interpretation, and reporting."],
  ["Culturally Responsive Methods", "We employ approaches that respect cultural context, community voice, and lived experience, ensuring that findings accurately reflect diverse populations."],
  ["Inclusive Data Collection", "Tools and instruments are designed to be accessible, linguistically appropriate, and sensitive to the needs of individuals with disabilities and historically marginalized groups."],
  ["Bias Mitigation", "We implement safeguards to reduce analytic bias, ensure fairness in interpretation, and promote transparency in methodology."],
  ["Accessible Deliverables", "Reports, dashboards, and presentations adhere to accessibility standards, including Section 508 compliance, ensuring equitable access to information."],
  ["Organizational Integrity", "PHMEG fosters an internal environment that values diversity, promotes inclusion, and ensures equitable opportunities for staff, partners, and collaborators."],
];

function MailLink({ email }) {
  return (
    <a href={`mailto:${email}`} className="link-red break-all">{email}</a>
  );
}

export default function Compliance() {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.replace("#", ""));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    } else {
      window.scrollTo({ top: 0 });
    }
  }, [hash]);

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-20" data-testid="compliance-page">
      {/* Header */}
      <div className="mb-12">
        <div className="overline text-[var(--red)] mb-3">Legal &amp; Compliance</div>
        <h1 className="font-display text-4xl md:text-5xl font-semibold tracking-tight">
          Compliance &amp; Policies
        </h1>
        <p className="text-[var(--text-2)] mt-4 max-w-2xl leading-relaxed">
          Public Health Masters Evaluation Group, LLC is committed to accessibility, privacy,
          and the responsible handling of information. The statements below outline our
          commitments and your rights.
        </p>
      </div>

      <div className="grid lg:grid-cols-[220px_1fr] gap-12">
        {/* Sticky nav */}
        <nav className="hidden lg:block" aria-label="Compliance sections">
          <div className="sticky top-24 space-y-1">
            {SECTIONS.map((s) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                className="flex items-center gap-2 text-sm text-[var(--text-2)] hover:text-white px-3 py-2 rounded-md hover:bg-white/5 transition-colors"
                data-testid={`compliance-nav-${s.id}`}
              >
                <s.icon className="w-4 h-4 text-[var(--silver)]" /> {s.label}
              </a>
            ))}
          </div>
        </nav>

        {/* Content */}
        <div className="space-y-16">
          {/* ADA */}
          <section id="ada" className="scroll-mt-24" data-testid="compliance-ada">
            <h2 className="font-display text-2xl font-semibold flex items-center gap-3 mb-5">
              <Accessibility className="w-6 h-6 text-[var(--red)]" /> ADA Compliance Statement
            </h2>
            <div className="space-y-4 text-[var(--text-2)] leading-relaxed">
              <p>
                Public Health Masters Evaluation Group, LLC (PHMEG) is committed to ensuring
                that our website — publichealthmaster.org and publichealthmasters.org — is
                accessible to all individuals, including people with disabilities. We strive to
                meet or exceed the requirements of the Americans with Disabilities Act (ADA) and
                follow the Web Content Accessibility Guidelines (WCAG) 2.1 AA as our accessibility
                standard.
              </p>
              <h3 className="text-white font-medium pt-2">Our Accessibility Commitments</h3>
              <ul className="list-disc pl-5 space-y-2">
                <li>We design and maintain our website to support screen readers, keyboard navigation, and assistive technologies.</li>
                <li>We use clear headings, structured content, and high contrast text to improve readability.</li>
                <li>We provide alternative text for images and descriptive labels for interactive elements.</li>
                <li>We continuously review our website to identify and correct accessibility barriers.</li>
              </ul>
              <h3 className="text-white font-medium pt-2">Requesting Assistance or Reporting an Issue</h3>
              <p>
                If you experience difficulty accessing any content on our website, or if you need
                information in an alternative format, PHMEG will assist you. Contact us at{" "}
                <MailLink email="support@publichealthmasters.org" />.
              </p>
              <p>We will respond promptly and work to provide the information or accommodation you need.</p>
            </div>
          </section>

          {/* Privacy */}
          <section id="privacy" className="scroll-mt-24" data-testid="compliance-privacy">
            <h2 className="font-display text-2xl font-semibold flex items-center gap-3 mb-5">
              <Lock className="w-6 h-6 text-[var(--red)]" /> Privacy Statement
            </h2>
            <div className="space-y-4 text-[var(--text-2)] leading-relaxed">
              <p>
                Public Health Masters Evaluation Group, LLC (PHMEG) respects your privacy and is
                committed to protecting the personal information you share with us when visiting
                publichealthmaster.org or publichealthmasters.org.
              </p>
              <h3 className="text-white font-medium pt-2">Information We Collect</h3>
              <p>We may collect limited information such as:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Voluntary contact information submitted through forms</li>
                <li>Basic website analytics (e.g., pages visited, browser type, general location)</li>
                <li>Information required to respond to inquiries or provide requested services</li>
              </ul>
              <p>We do not sell, rent, or trade your personal information.</p>
              <h3 className="text-white font-medium pt-2">How We Use Your Information</h3>
              <p>PHMEG uses collected information to:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Respond to inquiries</li>
                <li>Improve website performance and user experience</li>
                <li>Provide requested services</li>
                <li>Maintain security and prevent misuse of our website</li>
              </ul>
              <h3 className="text-white font-medium pt-2">How We Protect Your Information</h3>
              <p>We use administrative, technical, and physical safeguards to protect your information, including:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Encrypted data transmission (HTTPS)</li>
                <li>Access controls and authentication</li>
                <li>Secure storage practices</li>
                <li>Routine security monitoring</li>
              </ul>
              <h3 className="text-white font-medium pt-2">Third Party Services</h3>
              <p>
                We may use trusted third party tools (e.g., analytics providers) that follow
                industry standard privacy and security practices. These services do not receive
                personal information unless you explicitly provide it.
              </p>
              <h3 className="text-white font-medium pt-2">Your Choices</h3>
              <p>
                You may request that we delete your information or provide details about what data
                we hold. Contact: <MailLink email="support@publichealthmasters.org" />.
              </p>
            </div>
          </section>

          {/* HIPAA */}
          <section id="hipaa" className="scroll-mt-24" data-testid="compliance-hipaa">
            <h2 className="font-display text-2xl font-semibold flex items-center gap-3 mb-5">
              <ShieldCheck className="w-6 h-6 text-[var(--red)]" /> HIPAA Compliance Statement
            </h2>
            <div className="space-y-4 text-[var(--text-2)] leading-relaxed">
              <p>
                Public Health Masters Evaluation Group, LLC (PHMEG) provides public health
                evaluation, data analysis, and consulting services. While our website is not
                intended to collect Protected Health Information (PHI), PHMEG maintains strict
                safeguards to ensure HIPAA aligned practices when PHI is handled as part of
                contracted services.
              </p>
              <h3 className="text-white font-medium pt-2">Our HIPAA Aligned Commitments</h3>
              <ul className="list-disc pl-5 space-y-2">
                <li>PHMEG implements administrative, technical, and physical safeguards consistent with the Health Insurance Portability and Accountability Act (HIPAA).</li>
                <li>We protect PHI through secure data handling, encryption, access controls, and confidentiality protocols.</li>
                <li>PHMEG enters into Business Associate Agreements (BAAs) when required by federal law or client contracts.</li>
                <li>PHMEG limits access to PHI to authorized personnel only and follows minimum necessary standards.</li>
              </ul>
              <h3 className="text-white font-medium pt-2">Website Use and PHI</h3>
              <p>
                Our websites — publichealthmaster.org and publichealthmasters.org — are
                informational and not designed to receive medical records or PHI from the general
                public. Please do not submit PHI through contact forms, email, or general inquiry
                channels.
              </p>
              <p>
                For HIPAA related questions or secure communication channels, contact:{" "}
                <MailLink email="support@publichealthmasters.org" />.
              </p>
            </div>
          </section>

          {/* Equity */}
          <section id="security" className="scroll-mt-24" data-testid="compliance-security">
            <h2 className="font-display text-2xl font-semibold flex items-center gap-3 mb-5">
              <ShieldCheck className="w-6 h-6 text-[var(--red)]" /> Security Controls Status Note
            </h2>
            <div className="space-y-4 text-[var(--text-2)] leading-relaxed">
              <p>
                PHMEG is aligning core application safeguards with foundational access control, audit,
                boundary protection, transmission protection, and secure information handling practices
                commonly associated with CMMC Level 1 and NIST SP 800-171 Rev. 2 basic safeguarding expectations.
              </p>
              <p>
                Current application-level controls include authenticated member/admin gating, rate limiting on
                public and member-sensitive routes, PDF type and size validation, security headers, controlled
                document downloads, and basic security audit logging for sensitive generation workflows.
              </p>
              <p>
                Placeholder note: full organizational compliance also depends on non-code controls such as policy,
                workforce training, endpoint management, incident handling, account governance, backup review, and
                vendor/operational procedures. Those items are tracked separately and are not represented by the website alone.
              </p>
            </div>
          </section>

          {/* Equity */}
          <section id="equity" className="scroll-mt-24" data-testid="compliance-equity">
            <h2 className="font-display text-2xl font-semibold flex items-center gap-3 mb-5">
              <Scale className="w-6 h-6 text-[var(--red)]" /> Equity Commitment Statement
            </h2>
            <div className="space-y-4 text-[var(--text-2)] leading-relaxed">
              <p>
                Public Health Masters Evaluation Group, LLC is committed to promoting equitable access, treatment, and
                outcomes across all public health programs and services. We acknowledge that social, racial,
                educational, and health system inequities disproportionately affect vulnerable and historically
                underserved populations.
              </p>
              <p>
                PHMEG incorporates equity principles into all evaluation activities, ensuring that data collection,
                analysis, reporting, and recommendations reflect fairness, transparency, and respect for diverse
                communities. Our processes align with federal and state guidelines related to nondiscrimination,
                accessibility, and ethical public health practice.
              </p>
              <p>
                We remain dedicated to supporting partners in reducing disparities and strengthening systems that uphold
                equitable standards for all individuals.
              </p>
            </div>
          </section>

          {/* DEIA */}
          <section id="deia" className="scroll-mt-24" data-testid="compliance-deia">
            <h2 className="font-display text-2xl font-semibold flex items-center gap-3 mb-5">
              <Users className="w-6 h-6 text-[var(--red)]" /> Diversity, Equity, Inclusion &amp; Accessibility (DEIA) Policy
            </h2>
            <div className="space-y-4 text-[var(--text-2)] leading-relaxed">
              <p>
                Public Health Masters Evaluation Group LLC is committed to advancing diversity, equity, inclusion, and
                accessibility across all evaluation activities, organizational practices, and client engagements. We
                recognize that inequities in public health, education, and social systems are rooted in structural and
                institutional barriers. Our responsibility is to ensure that our work actively identifies, addresses,
                and mitigates these barriers.
              </p>
              <h3 className="text-white font-medium pt-2">Commitments</h3>
              <ul className="space-y-3">
                {DEIA_COMMITMENTS.map(([label, desc]) => (
                  <li key={label} className="flex gap-3">
                    <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[var(--red)] shrink-0" />
                    <span>
                      <span className="text-white font-medium">{label}:</span> {desc}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* Terms */}
          <section id="terms" className="scroll-mt-24" data-testid="compliance-terms">
            <h2 className="font-display text-2xl font-semibold flex items-center gap-3 mb-5">
              <FileText className="w-6 h-6 text-[var(--red)]" /> Terms of Use
            </h2>
            <div className="space-y-4 text-[var(--text-2)] leading-relaxed">
              <p>
                By accessing or using publichealthmaster.org or publichealthmasters.org (the
                &quot;Website&quot;) and any services offered by Public Health Masters Evaluation Group, LLC
                (PHMEG), you agree to these Terms of Use. If you do not agree, please do not use the
                Website.
              </p>
              <h3 className="text-white font-medium pt-2">Use of the Website</h3>
              <p>
                You agree to use the Website for lawful purposes only and not to interfere with its
                operation, security, or availability. Content on this Website is provided for
                general informational purposes and does not constitute professional, legal, or
                medical advice.
              </p>
              <h3 className="text-white font-medium pt-2">Intellectual Property</h3>
              <p>
                All content, branding, logos, text, and materials on this Website are the property
                of PHMEG or its licensors and may not be reproduced, distributed, or used without
                prior written permission.
              </p>
              <h3 className="text-white font-medium pt-2">Services, Bookings &amp; Payments</h3>
              <p>
                Consultations, evaluation packages, and invoices presented on this Website are
                subject to separate engagement agreements. Payments are processed securely through
                our third-party payment processor (Stripe). Scope, deliverables, and refund terms
                for paid engagements are governed by the applicable service agreement or invoice.
              </p>
              <h3 className="text-white font-medium pt-2">Disclaimers &amp; Limitation of Liability</h3>
              <p>
                The Website is provided &quot;as is&quot; without warranties of any kind. To the fullest
                extent permitted by law, PHMEG is not liable for any indirect, incidental, or
                consequential damages arising from your use of the Website.
              </p>
              <h3 className="text-white font-medium pt-2">Governing Law</h3>
              <p>
                These Terms are governed by the laws of the State of Nevada, without regard to its
                conflict of law principles.
              </p>
              <h3 className="text-white font-medium pt-2">Changes to These Terms</h3>
              <p>
                PHMEG may update these Terms from time to time. Continued use of the Website after
                changes are posted constitutes acceptance of the revised Terms.
              </p>
              <h3 className="text-white font-medium pt-2">Contact</h3>
              <p className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-[var(--silver)]" />
                <MailLink email="contact@publichealthmaster.org" />
              </p>
            </div>
          </section>

          <div className="text-xs text-[var(--text-3)] border-t border-[var(--border)] pt-6">
            Last updated {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}.
          </div>
        </div>
      </div>
    </div>
  );
}
